import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../config/firebase';
import { useStudyStore } from '../../stores/studyStore';
import { StudyDocument } from '@voxapilot/shared';
import { ROUTES } from '../../config/constants';
import { toast } from '../../components/ui/Toast';
import { StudyLayout } from '../../components/study/StudyLayout';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { DocumentViewer } from '../../components/study/DocumentViewer';

export function StudyDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<StudyDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentDocument } = useStudyStore();

  useEffect(() => {
    if (id) {
      loadDocument();
    }
  }, [id]);

  const loadDocument = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.study.getDocument(id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to load document');
      }

      if (response.data) {
        setDocument(response.data);
        setCurrentDocument(response.data);
      }
    } catch (err: any) {
      console.error('Load document error:', err);
      setError(err.message || 'Failed to load document');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!id) return;

    try {
      setIsAnalyzing(true);
      setError(null);

      const response = await api.study.analyzeDocument(id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to analyze document');
      }

      if (response.data) {
        setDocument(response.data);
        setCurrentDocument(response.data);
        toast.success('Document analyzed successfully!');
      }
    } catch (err: any) {
      console.error('Analyze error:', err);
      setError(err.message || 'Failed to analyze document');
      toast.error(err.message || 'Failed to analyze document');
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (isLoading) {
    return (
      <StudyLayout>
        <div className="p-6">
          <Container>
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-textSecondary dark:text-[#858585]">Loading document...</p>
              </div>
            </div>
          </Container>
        </div>
      </StudyLayout>
    );
  }

  if (error && !document) {
    return (
      <StudyLayout>
        <div className="p-6">
          <Container>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <Button onClick={() => navigate(ROUTES.STUDY_DOCUMENTS)} variant="outline">
                Back to Documents
              </Button>
            </div>
          </Container>
        </div>
      </StudyLayout>
    );
  }

  if (!document) {
    return (
      <StudyLayout>
        <div className="p-6">
          <Container>
            <div className="text-center py-12">
              <p className="text-textSecondary dark:text-[#858585] mb-4">Document not found</p>
              <Button onClick={() => navigate(ROUTES.STUDY_DOCUMENTS)} variant="outline">
                Back to Documents
              </Button>
            </div>
          </Container>
        </div>
      </StudyLayout>
    );
  }

  const needsAnalysis = document.status === 'uploading' || document.status === 'processing' || !document.formattedContent;
  const isCompleted = document.status === 'completed';

  return (
    <StudyLayout>
      <div className="p-6">
        <Container>
          {/* Header with Actions */}
          <div className="mb-6 flex items-center justify-between">
            <Button
              onClick={() => navigate(ROUTES.STUDY_DOCUMENTS)}
              variant="outline"
              size="sm"
            >
              ← Back
            </Button>

            <div className="flex items-center gap-3">
              {needsAnalysis && (
                <Button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  variant="primary"
                  size="sm"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Document'}
                </Button>
              )}
              {isCompleted && (
                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                  ✓ Analyzed
                </span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Document Content */}
          <div className="bg-white dark:bg-[#1E1E1E] rounded-lg border border-gray-200 dark:border-gray-700 p-8">
            {needsAnalysis && !isAnalyzing ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                  Document Ready for Analysis
                </h2>
                <p className="text-textSecondary dark:text-[#858585] mb-6">
                  Click "Analyze Document" to extract text, format content, and identify key concepts.
                </p>
                <Button onClick={handleAnalyze} variant="primary">
                  Analyze Document
                </Button>
              </div>
            ) : isAnalyzing ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-textSecondary dark:text-[#858585]">
                  Analyzing document... This may take a moment.
                </p>
              </div>
            ) : (
              <DocumentViewer document={document} />
            )}
          </div>
        </Container>
      </div>
    </StudyLayout>
  );
}
